import {BookedStatus, Workspace, WorkspaceBookType} from '@innowise-group/core';
import setHours from 'date-fns/setHours';
import {format} from 'date-fns';

interface UpdatedBookedWorkspace {
  workspace: number;
  occupation_start_date: string;
  comment: string;
  id: number;
  employee: number;
  occupation_end_date: string;
}

export interface UpdatedWorkspaceDataResult {
  number: number;
  rotate_degree: number;
  occupation_start_date: string;
  coordinates: Array<number[]>;
  comment: string;
  id: number;
  employee: number;
  room: number;
  status: WorkspaceBookType;
  occupation_end_date: string;
  booked_status?: UpdatedBookedWorkspace[];
  active_requests?: UpdatedBookedWorkspace[];
}

export interface OccupancyWorkspacePayload {
  id: number;
  status: WorkspaceBookType;
  employee: number | null;
  comment: string | null;
}

const OCCUPANCY_STATUSES: WorkspaceBookType[] = ['Occupied', 'Remote', 'Free', 'Reserved'];
const TEMPORAL_BOOKING_STATUSES: WorkspaceBookType[] = ['Booked', 'Coworking Booked'];

export const isOccupancyWorkspaceSave = (status?: WorkspaceBookType): boolean =>
  Boolean(status && OCCUPANCY_STATUSES.includes(status));

export const requiresWholeFloorRefresh = (status?: WorkspaceBookType): boolean =>
  Boolean(status && (isOccupancyWorkspaceSave(status) || status === 'Booked' || status === 'Coworking Booked'));

export const toOccupancyWorkspacePayload = (workspace: Workspace): OccupancyWorkspacePayload => ({
  id: Number(workspace.id),
  status: workspace.status,
  employee: workspace.employee?.id ? Number(workspace.employee.id) : null,
  comment: workspace.comment ?? null,
});

const parseBookedRangeDay = (day?: string): string | null => {
  if (!day) {
    return null;
  }
  // bookedStatus ranges come as dd.MM.yyyy from V2/legacy booked_status DTO.
  if (day.includes('.')) {
    const [dd, mm, yyyy] = day.split('.').map(Number);
    if (!dd || !mm || !yyyy) {
      return null;
    }
    return format(new Date(yyyy, mm - 1, dd), 'yyyy-MM-dd');
  }
  return day;
};

const updateBookedWorkspace = (workspaceId: number, booking: BookedStatus): UpdatedBookedWorkspace => {
  const {id, employee, comment, range} = booking;
  const start = parseBookedRangeDay(range?.startDay);
  const end = parseBookedRangeDay(range?.endDay);
  return {
    id: id ? Number(id) : null,
    workspace: workspaceId,
    employee: employee?.id ? Number(employee?.id) : null,
    comment: comment ? comment : null,
    occupation_start_date: start ? setHours(new Date(start), 12).toISOString() : null,
    occupation_end_date: end ? setHours(new Date(end), 21).toISOString() : null,
  };
};

const hydrateTemporalBookingFields = (
  payload: UpdatedWorkspaceDataResult,
  bookedStatus: BookedStatus[],
): UpdatedWorkspaceDataResult => {
  if (!TEMPORAL_BOOKING_STATUSES.includes(payload.status) || !bookedStatus.length) {
    return payload;
  }

  const primary = updateBookedWorkspace(payload.id, bookedStatus[0]);
  return {
    ...payload,
    employee: payload.employee ?? primary.employee,
    comment: payload.comment ?? primary.comment,
    occupation_start_date: payload.occupation_start_date ?? primary.occupation_start_date,
    occupation_end_date: payload.occupation_end_date ?? primary.occupation_end_date,
  };
};

export const updateWorkspaceData = (
  workspace: Workspace,
  multipleUpdate: boolean = false,
): UpdatedWorkspaceDataResult => {
  const {
    number,
    status,
    room,
    id,
    employee,
    comment,
    range,
    rotateDegree,
    coordinates,
    bookedStatus = [],
    activeRequests = [],
  } = workspace;
  const workspaceId = id ? Number(id) : null;
  let updatedWorkspace: UpdatedWorkspaceDataResult = {
    id: workspaceId,
    number: number ? Number(number) : null,
    status: status,
    employee: employee?.id ? Number(employee?.id) : null,
    room: room?.id ? Number(room.id) : null,
    occupation_start_date: range?.startDay ? setHours(new Date(range?.startDay), 9).toISOString() : null,
    occupation_end_date: range?.endDay ? setHours(new Date(range?.endDay), 18).toISOString() : null,
    coordinates,
    comment: comment ? comment : null,
    rotate_degree: rotateDegree,
  };

  if (multipleUpdate) {
    // Cancel / multiple-update must restore Booked from nested bookedStatus (V2 DTO),
    // because Workspace.employee/dates are empty for temporal desks.
    updatedWorkspace = hydrateTemporalBookingFields(updatedWorkspace, bookedStatus);
    updatedWorkspace.booked_status =
      bookedStatus.length > 0 ? bookedStatus.map((booking) => updateBookedWorkspace(workspaceId, booking)) : [];
    updatedWorkspace.active_requests =
      activeRequests.length > 0 ? activeRequests.map((booking) => updateBookedWorkspace(workspaceId, booking)) : [];
  }

  return updatedWorkspace;
};
